/**
 * Mehrsprachige Inhalte — geteilte Konventionen für mgmgnt und studio.
 *
 * Diese Datei existiert zweimal (mgmgnt/frontend und studio/frontend), analog zu
 * aiContent.ts. Sie ist bewusst abhängigkeitsfrei, damit beide Kopien identisch
 * bleiben können. Der studio-Server hat eine dritte, minimale Kopie der
 * Pfadlogik nicht nötig: der Import trägt `translations` nur durch, ausgewertet
 * wird erst im Browser.
 *
 * ── Speicherform ──────────────────────────────────────────────────────────────
 * Die bestehenden Spalten (name/title/description, options-Blob) bleiben die
 * BASISSPRACHE. Übersetzungen liegen additiv in der Spalte `translations`:
 *
 *   { "en": { "name": "…", "title": "…", "description": "…",
 *             "fields": { "categories.<catId>.exercises.<exId>.name": "…" } } }
 *
 * Additiv heißt: jeder bestehende Leser, der `translations` nicht kennt, sieht
 * weiterhin die Basissprache. Ein fehlender Eintrag fällt auf die Basis zurück —
 * eine halb übersetzte Vorlage ist deshalb nie kaputt, nur teilweise übersetzt.
 *
 * ── Warum flache Pfade statt eines gespiegelten Baums ─────────────────────────
 * Der Eingangscheck-Blob ist tief verschachtelt. Ein zweiter, gespiegelter Baum
 * müsste bei jeder Struktur­änderung mitgepflegt werden und driftet garantiert.
 * Die Pfade adressieren stattdessen über die stabilen Element-Ids, nicht über
 * Array-Indizes — Umsortieren im Editor verschiebt damit keine Übersetzung.
 * Nur wirklich id-lose Listen (focusAreas, matrixColumns, scoreKey) laufen über
 * den Index, weil es dort nichts Stabileres gibt.
 */

/** Sprache, auf die zurückgefallen wird, wenn keine Basissprache gepflegt ist. */
export const FALLBACK_LANG = 'de'

export interface LanguageDef {
  code: string
  label: string
  native_label?: string
  is_default?: number | boolean
  sort_order?: number
  archive?: number | boolean
}

/** Aktive Sprachen, Basissprache zuerst. */
export function activeLanguages(rows: any[] | undefined | null): LanguageDef[] {
  return (rows || [])
    .filter((r: any) => r && !r.archive && r.code)
    .slice()
    .sort((a: any, b: any) => {
      const ad = a.is_default ? 0 : 1
      const bd = b.is_default ? 0 : 1
      if (ad !== bd) return ad - bd
      const as = Number(a.sort_order || 0)
      const bs = Number(b.sort_order || 0)
      if (as !== bs) return as - bs
      return String(a.code).localeCompare(String(b.code))
    })
}

/** Code der Basissprache — die Sprache, in der die Basisspalten gepflegt sind. */
export function baseLang(rows: any[] | undefined | null): string {
  const langs = activeLanguages(rows)
  return langs.find(l => l.is_default)?.code || langs[0]?.code || FALLBACK_LANG
}

export function langLabel(rows: any[] | undefined | null, code: string): string {
  return activeLanguages(rows).find(l => l.code === code)?.label || code
}

/** `translations` ist TEXT und nicht garantiert valides JSON (Altzeilen). */
export function parseTranslations(row: any): Record<string, any> {
  const raw = row && row.translations
  if (!raw) return {}
  if (typeof raw === 'object') return raw as Record<string, any>
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/** Übersetzungsblock einer Sprache (immer ein Objekt, nie undefined). */
export function langBlock(row: any, lang: string): Record<string, any> {
  const block = parseTranslations(row)[lang]
  return block && typeof block === 'object' ? block : {}
}

/**
 * Feldwert in der gewünschten Sprache, mit Fallback auf die Basisspalte.
 * `field` ist der Spaltenname (name, title, description).
 */
export function tField(row: any, field: string, lang: string, base = FALLBACK_LANG): string {
  if (!row) return ''
  if (lang && lang !== base) {
    const value = langBlock(row, lang)[field]
    if (typeof value === 'string' && value.trim()) return value
  }
  return row[field] == null ? '' : String(row[field])
}

/** Setzt einen Feldwert in `translations` und gibt den neuen JSON-String zurück. */
export function setTranslatedField(row: any, field: string, lang: string, value: string): string {
  const all = parseTranslations(row)
  const block = { ...(all[lang] && typeof all[lang] === 'object' ? all[lang] : {}) }
  if (value && value.trim()) block[field] = value
  else delete block[field]
  all[lang] = block
  return JSON.stringify(all)
}

/**
 * Wie tField(), aber für Studio-Zeilen ohne eigene translations-SPALTE: dort
 * liegt der Block im options-JSON (workouts_exercises aus dem Vorlagen-Import).
 */
export function tOption(row: any, field: string, lang: string, base = FALLBACK_LANG): string {
  if (!row) return ''
  if (lang && lang !== base) {
    let opts: any = row.options
    if (typeof opts === 'string') { try { opts = JSON.parse(opts) } catch { opts = null } }
    const value = opts && opts.translations && opts.translations[lang] && opts.translations[lang][field]
    if (typeof value === 'string' && value.trim()) return value
  }
  return row[field] == null ? '' : String(row[field])
}

// ─── Eingangscheck: Texte im options-Blob ─────────────────────────────────────

export interface EcTextRef {
  /** Stabiler Pfad, Schlüssel in translations[lang].fields */
  path: string
  /** Text der Basissprache — dient als Platzhalter/Referenz im Editor */
  base: string
  /** Gruppenüberschrift für die Editor-Darstellung */
  group: string
  /** Feldbezeichnung innerhalb der Gruppe */
  label: string
  /** true → mehrzeiliges Feld */
  multiline?: boolean
  /** Schreibt den Wert zurück in den Blob (nur für applyFields verwendet) */
  set: (value: string) => void
}

/**
 * Läuft über jeden übersetzbaren Text eines Eingangscheck-options-Blobs.
 *
 * Die Reihenfolge folgt dem Editor-Aufbau, damit die Übersetzungsansicht in
 * derselben Reihenfolge liest wie die Basisansicht.
 */
export function eachEingangscheckText(options: any, visit: (ref: EcTextRef) => void): void {
  if (!options || typeof options !== 'object') return
  const o = options as any

  const emit = (
    path: string,
    holder: any,
    key: string | number,
    group: string,
    label: string,
    multiline = false,
  ) => {
    const current = holder?.[key]
    if (typeof current !== 'string' || !current.trim()) return
    visit({ path, base: current, group, label, multiline, set: v => { holder[key] = v } })
  }

  emit('name', o, 'name', 'Allgemein', 'Name')

  for (const m of (o.measurements || [])) {
    if (m && m.key) emit(`measurements.${m.key}.label`, m, 'label', 'Messwerte', m.label || m.key)
  }

  for (const cat of (o.categories || [])) {
    if (!cat) continue
    const catId = cat.id
    if (!catId) continue
    const catGroup = `Kategorie: ${cat.name || '—'}`
    emit(`categories.${catId}.name`, cat, 'name', catGroup, 'Kategoriename')
    emit(`categories.${catId}.note`, cat, 'note', catGroup, 'Hinweis', true)

    // Matrixspalten haben keine Id — hier ist der Index das Stabilste.
    for (let i = 0; i < (cat.matrixColumns || []).length; i++) {
      emit(`categories.${catId}.matrixColumns.${i}`, cat.matrixColumns, i, catGroup, `Matrixspalte ${i + 1}`)
    }
    for (const row of (cat.matrixRows || [])) {
      if (row && row.id) emit(`categories.${catId}.matrixRows.${row.id}.label`, row, 'label', catGroup, 'Beobachtungszeile')
    }
    for (const item of (cat.checklist || [])) {
      if (item && item.id) emit(`categories.${catId}.checklist.${item.id}.label`, item, 'label', catGroup, 'Checkliste')
    }

    for (const ex of (cat.exercises || [])) {
      if (!ex || !ex.id) continue
      const exBase = `categories.${catId}.exercises.${ex.id}`
      const exGroup = `${catGroup} › ${ex.name || 'Übung'}`
      emit(`${exBase}.name`, ex, 'name', exGroup, 'Name')
      emit(`${exBase}.description`, ex, 'description', exGroup, 'Kurzbeschreibung', true)
      emit(`${exBase}.criteria`, ex, 'criteria', exGroup, 'Abbruch-/Qualitätskriterium', true)
      // Punktstufen haben keine Id — der Punktwert IST der stabile Schlüssel.
      for (let i = 0; i < (ex.scoreLevels || []).length; i++) {
        emit(`${exBase}.scoreLevels.${i}`, ex.scoreLevels, i, exGroup, `Punktstufe ${i}`, true)
      }

      for (const d of (ex.descriptions || [])) {
        if (!d || !d.id) continue
        emit(`${exBase}.descriptions.${d.id}.title`, d, 'title', exGroup, 'Beschreibung — Titel')
        emit(`${exBase}.descriptions.${d.id}.text`, d, 'text', exGroup, 'Beschreibung — Text', true)
      }
      for (const md of (ex.media || [])) {
        if (md && md.id) emit(`${exBase}.media.${md.id}.description`, md, 'description', exGroup, 'Medienbeschreibung')
      }
      for (const p of (ex.parameters || [])) {
        if (p && p.id) emit(`${exBase}.parameters.${p.id}.label`, p, 'label', exGroup, 'Parameter')
      }
    }
  }

  for (const n of (o.noteFields || [])) {
    if (!n || !n.id) continue
    emit(`noteFields.${n.id}.label`, n, 'label', 'Freitextfelder', n.label || n.id)
    emit(`noteFields.${n.id}.placeholder`, n, 'placeholder', 'Freitextfelder', `${n.label || n.id} — Platzhalter`)
  }

  for (let i = 0; i < (o.focusAreas || []).length; i++) {
    emit(`focusAreas.${i}`, o.focusAreas, i, 'Trainingsschwerpunkte', `Schwerpunkt ${i + 1}`)
  }
  for (let i = 0; i < (o.scoreKey || []).length; i++) {
    const band = o.scoreKey[i]
    if (!band) continue
    emit(`scoreKey.${i}.label`, band, 'label', 'Score-Skala', `Stufe ${i + 1}`)
    emit(`scoreKey.${i}.hint`, band, 'hint', 'Score-Skala', `Stufe ${i + 1} — Interpretation`)
  }
}

/** Alle übersetzbaren Texte als Liste — Basis der Übersetzungsansicht. */
export function collectEingangscheckTexts(options: any): EcTextRef[] {
  const refs: EcTextRef[] = []
  eachEingangscheckText(options, r => refs.push(r))
  return refs
}

/**
 * Wendet eine Pfad-Map auf eine KOPIE des Blobs an und gibt sie zurück.
 * Unbekannte Pfade (Struktur hat sich geändert) werden still ignoriert — eine
 * verwaiste Übersetzung darf die Anzeige nie kaputt machen.
 */
export function applyEingangscheckFields(options: any, fields: Record<string, string> | undefined | null): any {
  if (!options) return options
  if (!fields || !Object.keys(fields).length) return options
  const clone = JSON.parse(JSON.stringify(options))
  eachEingangscheckText(clone, ref => {
    const translated = fields[ref.path]
    if (typeof translated === 'string' && translated.trim()) ref.set(translated)
  })
  return clone
}

/**
 * Eingangscheck-Blob in der gewünschten Sprache.
 * `row` ist entweder eine mgmt_eingangscheck_templates-Zeile (translations als
 * Spalte) oder ein importierter Studio-Blob (translations im options-JSON).
 */
export function localizeEingangscheck(options: any, translations: any, lang: string, base = FALLBACK_LANG): any {
  if (!lang || lang === base) return options
  const all = translations && typeof translations === 'object'
    ? translations
    : (() => { try { return JSON.parse(translations || '{}') } catch { return {} } })()
  const block = all[lang]
  if (!block || typeof block !== 'object') return options
  const localized = applyEingangscheckFields(options, block.fields)
  if (typeof block.name === 'string' && block.name.trim()) localized.name = block.name
  return localized
}
